import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, FolderKanban, FileText, DollarSign,
  AlertCircle, Presentation, Clock, CheckCircle2,
  TrendingUp, ArrowRight, ChevronRight, Search,
} from 'lucide-react';
import { BRAND_NAME } from '../../lib/brandConfig';
import { API } from '../../lib/api';

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmt$(num) {
  if (num == null) return '$0';
  return '$' + Number(num).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtDate(iso) {
  if (!iso) return '—';
  return iso.slice(0, 10);
}
function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return fmtDate(iso);
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, color, onClick }) {
  return (
    <div
      onClick={onClick}
      className="rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md"
      style={{
        background: '#ffffff',
        borderColor: '#e5e4df',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6b7280' }}>
          {label}
        </span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="text-2xl font-bold" style={{ color: '#1a1a2e', fontFamily: "'Space Grotesk', sans-serif" }}>
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1" style={{ color: '#6b7280' }}>{sub}</div>
      )}
    </div>
  );
}

// ── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ title, icon: Icon, children, actionLabel, onAction }) {
  return (
    <div className="rounded-lg border overflow-hidden" style={{ background: '#ffffff', borderColor: '#e5e4df' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#e5e4df', background: '#f8f7f4' }}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: '#c9a84c' }} />
          <span className="text-sm font-semibold" style={{ color: '#1a1a2e' }}>{title}</span>
        </div>
        {actionLabel && onAction && (
          <button onClick={onAction} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: '#c9a84c' }}>
            {actionLabel} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

// ── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const colors = {
    draft: { bg: '#f3f4f6', text: '#6b7280' },
    sent: { bg: '#dbeafe', text: '#1d4ed8' },
    paid: { bg: '#dcfce7', text: '#16a34a' },
    accepted: { bg: '#dcfce7', text: '#16a34a' },
    declined: { bg: '#fee2e2', text: '#dc2626' },
    planning: { bg: '#dbeafe', text: '#1d4ed8' },
    active: { bg: '#dcfce7', text: '#16a34a' },
    completed: { bg: '#f3f4f6', text: '#6b7280' },
    on_hold: { bg: '#fef3c7', text: '#d97706' },
  };
  const c = colors[status] || colors.draft;
  return (
    <span
      className="inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded"
      style={{ background: c.bg, color: c.text }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// ── Row Item (clickable) ─────────────────────────────────────────────────────
function RowItem({ label, sub, right, onClick }) {
  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between py-2 px-1 cursor-pointer hover:bg-[#f8f7f4] rounded transition-colors group"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate" style={{ color: '#1a1a2e' }}>{label}</div>
        {sub && <div className="text-xs truncate" style={{ color: '#6b7280' }}>{sub}</div>}
      </div>
      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
        {right}
        <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#c9a84c' }} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export default function DashboardTab({ onNavigateTab }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Dashboard stats error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-sm" style={{ color: '#6b7280' }}>Loading dashboard...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 mx-auto mb-3" style={{ color: '#c9a84c', opacity: 0.4 }} />
        <p className="text-sm" style={{ color: '#6b7280' }}>Unable to load dashboard data</p>
      </div>
    );
  }

  const c = stats.counts || {};
  const rev = stats.revenue || {};

  return (
    <div className="space-y-6">
      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KpiCard
          icon={Building2}
          label="Active Clients"
          value={c.clients?.active ?? 0}
          sub={`${c.clients?.total ?? 0} total · ${c.clients?.lead ?? 0} leads`}
          color="#1a1a2e"
          onClick={() => onNavigateTab?.('clients')}
        />
        <KpiCard
          icon={FolderKanban}
          label="Active Projects"
          value={c.projects?.active ?? 0}
          sub={`${c.projects?.planning ?? 0} planning · ${c.projects?.completed ?? 0} completed`}
          color="#c9a84c"
          onClick={() => onNavigateTab?.('projects')}
        />
        <KpiCard
          icon={FileText}
          label="Total Quotes"
          value={c.quotes?.total ?? 0}
          sub={fmt$(rev.total_quote_value) + ' total value'}
          color="#3b82f6"
          onClick={() => onNavigateTab?.('quotes')}
        />
        <KpiCard
          icon={DollarSign}
          label="Paid Revenue"
          value={fmt$(rev.total_paid_revenue)}
          sub={`${c.invoices?.paid ?? 0} paid invoices`}
          color="#16a34a"
          onClick={() => onNavigateTab?.('invoices')}
        />
        <KpiCard
          icon={Clock}
          label="Outstanding"
          value={fmt$(rev.total_outstanding)}
          sub={`${c.invoices?.sent ?? 0} sent invoices`}
          color="#d97706"
          onClick={() => onNavigateTab?.('invoices')}
        />
        <KpiCard
          icon={Presentation}
          label="Proposals"
          value={c.proposals?.accepted ?? 0}
          sub={`${c.proposals?.total ?? 0} total · ${c.proposals?.draft ?? 0} draft`}
          color="#8b5cf6"
          onClick={() => onNavigateTab?.('proposals')}
        />
      </div>

      {/* ── Recent Activity (3-col) ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Recent Quotes */}
        <SectionCard
          title="Recent Quotes"
          icon={FileText}
          actionLabel="View All"
          onAction={() => onNavigateTab?.('quotes')}
        >
          {(stats.recent?.quotes || []).length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: '#6b7280' }}>No quotes yet</p>
          ) : (
            stats.recent.quotes.map(q => (
              <RowItem
                key={q.id}
                label={q.quote_no}
                sub={q.client_name || q.project_name || '—'}
                right={<span className="text-xs font-mono" style={{ color: '#1a1a2e' }}>{fmt$(q.subtotal)}</span>}
                onClick={() => navigate(`/quote/${q.id}`)}
              />
            ))
          )}
        </SectionCard>

        {/* Recent Invoices */}
        <SectionCard
          title="Recent Invoices"
          icon={DollarSign}
          actionLabel="View All"
          onAction={() => onNavigateTab?.('invoices')}
        >
          {(stats.recent?.invoices || []).length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: '#6b7280' }}>No invoices yet</p>
          ) : (
            stats.recent.invoices.map(inv => (
              <RowItem
                key={inv.id}
                label={inv.invoice_no}
                sub={inv.client_name || '—'}
                right={
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono" style={{ color: '#1a1a2e' }}>{fmt$(inv.subtotal)}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                }
                onClick={() => navigate(`/invoice/${inv.id}`)}
              />
            ))
          )}
        </SectionCard>

        {/* Recent Proposals */}
        <SectionCard
          title="Recent Proposals"
          icon={Presentation}
          actionLabel="View All"
          onAction={() => onNavigateTab?.('proposals')}
        >
          {(stats.recent?.proposals || []).length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: '#6b7280' }}>No proposals yet</p>
          ) : (
            stats.recent.proposals.map(p => (
              <RowItem
                key={p.id}
                label={p.proposal_no}
                sub={p.variables?.client_name || p.variables?.project_name || '—'}
                right={<StatusBadge status={p.status} />}
                onClick={() => navigate(`/proposal/${p.id}`)}
              />
            ))
          )}
        </SectionCard>
      </div>

      {/* ── Bottom Row: Invoices + Active Projects ─────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Due Soon & Overdue Invoices */}
        <SectionCard
          title="Invoices Due"
          icon={AlertCircle}
          actionLabel="View All"
          onAction={() => onNavigateTab?.('invoices')}
        >
          {(() => {
            const overdue = stats.overdue_invoices || [];
            const dueSoon = stats.due_soon_invoices || [];
            const all = [
              ...overdue.map(i => ({ ...i, _overdue: true })),
              ...dueSoon.filter(i => !overdue.find(o => o.id === i.id)),
            ];
            if (all.length === 0) {
              return <p className="text-xs py-4 text-center" style={{ color: '#6b7280' }}>No invoices due soon</p>;
            }
            return all.map(inv => (
              <RowItem
                key={inv.id}
                label={inv.invoice_no}
                sub={`${inv.client_name || '—'} · Due ${fmtDate(inv.due_date)}`}
                right={
                  <span
                    className="inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded"
                    style={{
                      background: inv._overdue ? '#fee2e2' : '#fef3c7',
                      color: inv._overdue ? '#dc2626' : '#d97706',
                    }}
                  >
                    {inv._overdue ? 'Overdue' : 'Due Soon'}
                  </span>
                }
                onClick={() => navigate(`/invoice/${inv.id}`)}
              />
            ));
          })()}
        </SectionCard>

        {/* Active Projects */}
        <SectionCard
          title="Active Projects"
          icon={FolderKanban}
          actionLabel="View All"
          onAction={() => onNavigateTab?.('projects')}
        >
          {(stats.active_projects || []).length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: '#6b7280' }}>No active projects</p>
          ) : (
            stats.active_projects.map(pr => (
              <RowItem
                key={pr.id}
                label={pr.project_name}
                sub={`${pr.client_name || '—'} · ${fmtDate(pr.start_date)}${pr.end_date ? ' → ' + fmtDate(pr.end_date) : ''}`}
                right={<StatusBadge status={pr.status} />}
                onClick={() => navigate(`/projects/${pr.id}`)}
              />
            ))
          )}
        </SectionCard>
      </div>

      {/* ── Quick Actions ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: '+ New Quote', path: '/quote/new', icon: FileText },
          { label: '+ New Invoice', path: '/invoice/new', icon: DollarSign },
          { label: '+ New Proposal', path: '/proposal/new', icon: Presentation },
          { label: '🔍 Search', tab: 'search', icon: Search },
          { label: '📋 Activity Log', tab: 'audit-logs', icon: Clock },
        ].map(btn => (
          <button
            key={btn.label}
            onClick={() => btn.path ? navigate(btn.path) : onNavigateTab?.(btn.tab)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded transition-colors"
            style={{
              background: '#1a1a2e',
              color: '#ffffff',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#2a2a4e'}
            onMouseLeave={e => e.currentTarget.style.background = '#1a1a2e'}
          >
            <btn.icon className="w-3.5 h-3.5" />
            {btn.label}
          </button>
        ))}
      </div>
    </div>
  );
}
